import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { PensionProvidersName } from '@nxsys/constants/AppConstants';
import {
  useGetPensionContributionReportList,
  useMarkAsSubmittedPension,
  useResubmitPension,
} from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetPensionContributionReportListParams } from '@nxsys/store/slices/report/types';
import {
  getAppConfig,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import PensionContributionFilterModal, {
  PensionContributionFilterValues,
} from '../../reports/pension/components/PensionContributionFilterModal';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction: () => void;
};

const PensionSubmissionErrorModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction,
}) => {
  const { messages } = useTypedIntl();
  const paymentModels = getAppConfig('paymentModels');
  const dispatch = useDispatch();
  const { payrollCountry, isBureau } = useSelector((state: AppState) => state.appStore);
  const { pensionContributionReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    pensionContributionReportList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IPensionSubmission[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const {
    run: getPensionContributionReportList,
    loading: getPensionContributionReportListLoading,
  } = useGetPensionContributionReportList();

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
        taxYear: currentTaxYear,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetPensionContributionReportListParams) => {
    const newParams = {
      ...params,
      pensionsError: 'TRUE',
      isPendingSubmitPension: true,
      pensionErrorType: 'SMART_PENSION_ENROLLMENT',
      reportType: 'pension',
      paymentModels: paymentModels?.map((item) => item.value),
    };
    dispatch(reportActions.getPensionContributionReportList(newParams));
  };

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: PensionContributionFilterValues) => {
    const params: GetPensionContributionReportListParams = {
      ...queryParams,
      payFrequency: values.payFrequency,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
    };
    handleFetchData(params);
  };

  const handleExportErrors = (data: IPensionSubmission[]) => {
    const dataExport = data.map((item) => {
      const {
        taxPeriod,
        taxYear,
        subcontractorId,
        subcontractor,
        agency,
        company,
        grossForPension,
        eePension,
        erPension,
        acv,
        contributedPension,
        contributePensionError,
      } = item;
      return {
        taxPeriod: taxPeriod || '',
        taxYear: taxYear || '',
        employeeId: subcontractorId,
        employeeName:
          subcontractor?.fullName ||
          `${subcontractor?.forename || ''} ${subcontractor?.surname || ''}`,
        agencyRef: `${agency?.agencyPrefix || '___'}/${subcontractor?.agencyRef || ''
          }`,
        paymentCompany: company?.companyName || '',
        grossForPension: formatMoneyExport(
          grossForPension,
          showCurrencyByCountry(payrollCountry),
        ),
        employeePension: formatMoneyExport(
          eePension,
          showCurrencyByCountry(payrollCountry),
        ),
        employersPension: formatMoneyExport(
          erPension,
          showCurrencyByCountry(payrollCountry),
        ),
        additionalVoluntaryContribution: formatMoneyExport(
          acv,
          showCurrencyByCountry(payrollCountry),
        ),
        totalContribution: formatMoneyExport(
          contributedPension,
          showCurrencyByCountry(payrollCountry),
        ),
        contributePensionError: contributePensionError || '',
      };
    });

    const headers = [
      'Tax Period',
      'Tax Year',
      'Employee ID',
      'Employee Name',
      'Agency Reference',
      'Payment Company',
      'Gross for Pension',
      'Employee Contribution',
      'Employer Contribution',
      'Additional Voluntary Contribution',
      'Total Contribution',
      'Error',
    ];

    const csvExporter = new ExportToCsv({
      filename:
        `Pension-errors-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}` || 'Download.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleExportErrorsSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExportErrors(selectedRows);
  };

  const handleExportAllErrors = () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    getPensionContributionReportList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      pensionsError: 'TRUE',
    }).then((res) => {
      handleExportErrors(res.data);
    });
  };

  const { run: resubmitPension, loading: resubmitPensionLoading } =
    useResubmitPension();
  const handleResubmitSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    resubmitPension({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.resubmitSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content: res.message,
        });
      }
      refetch();
    });
  };

  const {
    run: markAsSubmittedPension,
    loading: markAsSubmittedPensionLoading,
  } = useMarkAsSubmittedPension();
  const handleMarkAsSubmittedSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    markAsSubmittedPension({
      ids: selectedRows.map((item) => item.id),
    }).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.markAsSubmittedSuccess'],
        });
      } else {
        showMessage({
          type: 'error',
          content:
            res.message || messages['report.pension.markAsSubmittedError'],
        });
      }
      refetch();
    });
  };

  const columns: ColumnsType<IPensionSubmission> = [
    {
      title: messages['table.error'],
      dataIndex: 'contributePensionError',
      width: 350,
      render: (value: any) => value || '_',
    },
    {
      title: messages['common.taxPeriod'],
      align: 'right',
      render: (record) => `${record?.taxPeriod || ''}/${record?.taxYear || ''}`,
    },
    {
      title: messages['common.pensionProvider'],
      align: 'center',
      render: (record) =>
        PensionProvidersName[
        record?.pensionProvider as keyof typeof PensionProvidersName
        ] || '_',
    },
    {
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorId',
      render: (value) => value || '_',
    },
    {
      title: messages['table.employeeName'],
      dataIndex: 'subcontractor',
      render: (value) =>
        value?.fullName ||
        `${value?.forename || ''} ${value?.surname || ''}` ||
        '_',
    },
    {
      title: isBureau ? messages['table.employerRef'] : messages['table.agencyRef'],
      width: 200,
      render: (record) =>
        `${record?.agency?.agencyPrefix || '___'}/${record?.subcontractor?.agencyRef ||
        record?.subcontractor?.id ||
        '_'
        }`,
    },
    {
      title: isBureau ? messages['table.employer'] : messages['table.paymentCompany'],
      dataIndex: 'company',
      width: 200,
      render: (value) => value?.companyName || '_',
    },
    {
      title: messages['table.grossForPension'],
      align: 'right',
      dataIndex: 'grossForPension',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.employeeContribution'],
      dataIndex: 'eePension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },

    {
      title: messages['common.avcEmployee'],
      dataIndex: 'payroll',
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.payeeUmbrellaValue?.avcEEPension,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.employerContribution'],
      dataIndex: 'erPension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },

    {
      title: messages['common.avcEmployer'],
      dataIndex: 'payroll',
      align: 'right',
      render: (value) =>
        formatMoney(
          value?.payeeUmbrellaValue?.avcERPension,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      title: messages['table.additionalVoluntaryContribution'],
      width: 300,
      align: 'right',
      dataIndex: 'avc',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['table.totalContribution'],
      dataIndex: 'contributedPension',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className='whitespace-nowrap'>Pension Contribution Report</div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'exportErrors',
                  label: 'Export Errors',
                  onClick: handleExportErrorsSelected,
                },
                {
                  key: 'exportAllErrors',
                  label: 'Export All Errors',
                  onClick: handleExportAllErrors,
                },
                {
                  key: 'resubmit',
                  label: 'Resubmit',
                  onClick: handleResubmitSelected,
                },
                {
                  key: 'markAsSubmitted',
                  label: 'Mark as Submitted',
                  onClick: handleMarkAsSubmittedSelected,
                },
              ],
            }}
          >
            <AppButton
              name="Actions"
              rightIcon={<DownOutlined />}
              loading={
                getPensionContributionReportListLoading ||
                resubmitPensionLoading ||
                markAsSubmittedPensionLoading
              }
              disabled={
                getPensionContributionReportListLoading ||
                resubmitPensionLoading ||
                markAsSubmittedPensionLoading
              }
            />
          </Dropdown>
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <div className="flex justify-between flex-wrap w-full gap-4 my-[20px] px-[10px]">
        <div className="flex flex-wrap gap-4">
          <AppInputSearch onChange={handleSearch} />
          <AppMenuFilter
            options={[]}
            onChange={() => { }}
            onClick={() => setIsOpenFilterModal(true)}
          />
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <TaxYearSelect
            initValue={currentTaxYear}
            onFilter={(value) =>
              handleFetchData({ ...queryParams, taxYear: value.taxYear })
            }
          />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2700 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <PensionContributionFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppModalContainer>
  );
};

export default PensionSubmissionErrorModal;
